import type {
  MoneyView,
  ReportingOptions,
  ReportingUnit,
  SessionSegment,
} from "../../types/stationPerformance";

/** FR-002a: a site is the sum of its connectors; only the denominator of an average differs. */
export const REPORTING_UNIT_OPTIONS: { label: string; value: ReportingUnit }[] = [
  { label: "Per site", value: "SITE" },
  { label: "Per connector", value: "CONNECTOR" },
];

/** FR-002f: which population every measure is computed over. */
export const SEGMENT_OPTIONS: { label: string; value: SessionSegment }[] = [
  { label: "All sessions", value: "ALL" },
  { label: "Paid only", value: "PAID_ONLY" },
  { label: "Free only", value: "FREE_ONLY" },
];

/** FR-031: the whole amount taken, or cocharge's share of it. */
export const MONEY_VIEW_OPTIONS: { label: string; value: MoneyView }[] = [
  { label: "Total taken", value: "TOTAL" },
  { label: "cocharge share", value: "COCHARGE_SHARE" },
];

/** FR-002e: free-charge sessions are included by default. */
export const DEFAULT_OPTIONS: ReportingOptions = {
  unit: "SITE",
  segment: "ALL",
  moneyView: "TOTAL",
  includeFreeCharge: true,
  station: null,
  provider: null,
};

/** Persisted so a comparison begun per connector is not silently continued per site (FR-030a). */
export const OPTIONS_STORAGE_KEY = "cp388.stationPerformance.options";

export const MONTHLY_SORT_OPTIONS = [
  { label: "Money", value: "moneyCents" },
  { label: "Sessions", value: "sessions" },
  { label: "Energy", value: "energyKwh" },
  { label: "Energy per session", value: "energyPerSession" },
  { label: "Money per session", value: "moneyPerSessionCents" },
  { label: "Free share of energy", value: "freeShareOfEnergyPercent" },
  { label: "Station name", value: "stationName" },
];

export const LIFETIME_SORT_OPTIONS = [
  { label: "Lifetime money", value: "lifetime.moneyCents" },
  { label: "Lifetime sessions", value: "lifetime.sessions" },
  { label: "Lifetime energy", value: "lifetime.energyKwh" },
  { label: "Money per active month", value: "perActiveMonth.moneyCents" },
  { label: "Sessions per active month", value: "perActiveMonth.sessions" },
  { label: "Months active", value: "monthsWithActivity" },
  { label: "Free share of energy", value: "freeShareOfEnergyPercent" },
];

export const DISTRIBUTION_MEASURE_OPTIONS = [
  { label: "Money", value: "moneyCents" },
  { label: "Sessions", value: "sessions" },
  { label: "Energy", value: "energyKwh" },
  { label: "Energy per session", value: "energyPerSession" },
  { label: "Money per session", value: "moneyPerSessionCents" },
  { label: "Free share of energy", value: "freeShareOfEnergyPercent" },
];

export interface ChangeBand {
  /** Lower bound of the band. Ordered high to low; the first match wins. */
  from: number;
  /**
   * Whether [from] itself belongs to this band.
   *
   * Not decoration. "Down 50% or more is red" means -50 has to land in the red band, so the band above
   * it must *exclude* its own lower bound. "Up 50% or more is green" means +50 has to land in green, so
   * that band must *include* it. The two ends need opposite treatment, and matching everything with
   * `>=` silently put both boundary values one band too mild.
   */
  includeFrom: boolean;
  label: string;
  /** PrimeReact palette tokens, so the bands follow the app's theme rather than hardcoded hex. */
  background: string;
  text: string;
}

/**
 * Colour bands for a month-over-month percentage, strongest growth first.
 *
 * Discrete bands rather than a continuous gradient: a reader can decode "yellow means down" from the
 * legend, but nobody reads a precise number off a colour ramp, and a table where every cell is a
 * slightly different shade is decoration rather than information.
 *
 * The flat band around zero is deliberate. Without it a station that moved 0.4% gets painted as though
 * something happened, and the colour stops meaning anything.
 */
export const CHANGE_BANDS: ChangeBand[] = [
  { from: 50, includeFrom: true, label: "up 50% or more", background: "var(--green-100)", text: "var(--green-900)" },
  { from: 5, includeFrom: false, label: "up", background: "var(--blue-100)", text: "var(--blue-900)" },
  { from: -5, includeFrom: true, label: "flat", background: "var(--surface-200)", text: "var(--text-color-secondary)" },
  { from: -40, includeFrom: false, label: "down", background: "var(--yellow-100)", text: "var(--yellow-900)" },
  { from: -50, includeFrom: false, label: "down 40% or more", background: "var(--orange-200)", text: "var(--orange-900)" },
  { from: -Infinity, includeFrom: true, label: "down 50% or more", background: "var(--red-100)", text: "var(--red-900)" },
];

/** The band a percentage falls in. `invert` mirrors the sign where a rise is the bad news. */
export const bandFor = (percent: number, invert = false): ChangeBand => {
  const value = invert ? -percent : percent;
  return (
    CHANGE_BANDS.find((b) => (b.includeFrom ? value >= b.from : value > b.from)) ??
    CHANGE_BANDS[CHANGE_BANDS.length - 1]
  );
};

export interface SpreadBand {
  /** Upper bound of the band, on the coefficient of variation. Ordered tight to wild. */
  upTo: number;
  label: string;
  background: string;
  text: string;
}

/**
 * Colour bands for spread, keyed on the coefficient of variation rather than the standard deviation.
 *
 * Sigma alone cannot be banded: a sigma of 500 is wild for sessions per day and unremarkable for money
 * in cents, so any fixed threshold would just re-rank stations by size. The CV is sigma over the mean,
 * which is dimensionless and comparable, so it is the only one of the two that a colour can describe.
 *
 * CV of 1 is the line worth knowing: sigma has reached the mean, so sessions are so uneven that the
 * average has stopped describing a typical one.
 */
export const SPREAD_BANDS: SpreadBand[] = [
  { upTo: 0.25, label: "very consistent", background: "var(--green-100)", text: "var(--green-900)" },
  { upTo: 0.5, label: "consistent", background: "var(--blue-100)", text: "var(--blue-900)" },
  { upTo: 0.75, label: "uneven", background: "var(--yellow-100)", text: "var(--yellow-900)" },
  { upTo: 1, label: "very uneven", background: "var(--orange-200)", text: "var(--orange-900)" },
  {
    upTo: Infinity,
    label: "wildly uneven, the average is not describing a typical session",
    background: "var(--red-100)",
    text: "var(--red-900)",
  },
];

/** The spread band a coefficient of variation falls in. */
export const spreadBandFor = (coefficientOfVariation: number): SpreadBand =>
  SPREAD_BANDS.find((b) => coefficientOfVariation <= b.upTo) ?? SPREAD_BANDS[SPREAD_BANDS.length - 1];

/** Human wording for why a measure is undefined. Shown on hover, never as a number. */
export const NOT_APPLICABLE_REASONS: Record<string, string> = {
  NO_QUALIFYING_SESSIONS: "No sessions with energy in this period",
  NO_SESSIONS: "No sessions at all in this period",
  NO_PAID_SESSIONS: "No paid sessions — this unit charged only for free",
  SINGLE_OBSERVATION: "Only one observation, so there is no spread to measure",
  ZERO_MEAN: "The average is zero, so a relative spread is undefined",
  FREE_ONLY_SEGMENT: "Free charging generates no cash, so money does not apply",
  NEVER_COMPUTED: "This period has not been aggregated yet",
};

export const CLOSURE_STATE_LABELS: Record<string, string> = {
  OPEN: "Open — still running, figures will change",
  GRACE: "In grace — late sessions can still land",
  SEALED: "Sealed — figures are frozen",
};

/** Sealed is the reassuring state, so it reads neutral; open reads as "not final yet". */
export const CLOSURE_STATE_SEVERITY: Record<string, "info" | "warning" | "success"> = {
  OPEN: "warning",
  GRACE: "info",
  SEALED: "success",
};

/** CLR-296: exact wording for the cocharge share breakdown and the THG figures. Shared by the dashboard and later views. */
export const COCHARGE_REVENUE_TEXTS = {
  shareLabel: "cocharge share (fee + internal + THG)",
  feeLabel: "cocharge fee (15%)",
  internalRevenueLabel: "Internal revenue",
  thgShareLabel: "cocharge THG share",
  totalThgLabel: "Total THG",
  thgPayoutLabel: "THG payout",
  headlineNote:
    "Everything cocharge earns: our fee, our own stations' revenue and our part of the THG money.",
  feeExplanation: "Our 15% slice of what drivers paid at stations owned by others.",
  internalRevenueExplanation:
    "Everything drivers paid at stations owned by cocharge / HadSol GmbH — we keep all of it.",
  thgShareExplanation:
    "The THG money we keep: all of it for our own stations and free charging, plus the gap between the price we get and the price we pay owners.",
  totalThgExplanation: "All THG money cocharge receives: every kWh charged × the incoming THG price.",
  thgPayoutExplanation:
    "What we pass on to station owners: paid kWh at their stations × the outgoing THG price.",
  sumLine: "fee + internal revenue + THG share",
  notConfigured: "not configured",
  missingPricesNote: (years: number[]) =>
    `Leaves out THG for ${years.join(", ")}: no THG price is configured.`,
  creditNoteWarning: (year: number, oldPrice: string) =>
    `THG credit notes for ${year} have already been issued at ${oldPrice} €/MWh. Changing the outgoing price will not change them, and the payout list will no longer match what was paid.`,
  outgoingLocked: (year: number) =>
    `Outgoing price is locked: ${year} is final and THG credit notes have been issued.`,
  payoutsNote: (n: number) => `${n} internal stations are not paid.`,
  internalBadge: "Internal – not paid",
  internalPlain: "Internal",
  excludedInternal: (n: number) => `Excludes ${n} internal stations (not paid).`,
};

/**
 * "cocharge fee (15%)" with the live percent. `cochargeSharePercent` is deprecated and equals the fee
 * percent, so it only serves as a fallback for an older payload; with neither, the verbatim text stands.
 */
export const cochargeFeeLabel = (
  context: { cochargeFeePercent?: number | null; cochargeSharePercent?: number | null } | null | undefined,
): string => {
  const percent = context?.cochargeFeePercent ?? context?.cochargeSharePercent;
  return percent == null ? COCHARGE_REVENUE_TEXTS.feeLabel : `cocharge fee (${percent}%)`;
};

/**
 * The fee explanation with the live percent; the verbatim "15%" text stands when the percent is unknown.
 * Same fallback rule as `cochargeFeeLabel` (pass `context?.cochargeFeePercent ?? context?.cochargeSharePercent`).
 */
export const cochargeFeeExplanation = (percent?: number | null): string =>
  percent == null
    ? COCHARGE_REVENUE_TEXTS.feeExplanation
    : COCHARGE_REVENUE_TEXTS.feeExplanation.replace("15%", `${percent}%`);

/** Days covered with at most two decimals: 19.5, 2.63, 19. */
export const formatDays = (days: number): string => String(Number(days.toFixed(2)));

/** BOF-81 / BOF-84: month-end projection texts. */
export const PROJECTION_TEXTS = {
  projected: (formattedValue: string, monthEnd: string) => `≈ ${formattedValue} projected by ${monthEnd}`,
  hoverWithToday: (daysCovered: number, daysInMonth: number, todayMinutes: number) =>
    `so far ÷ ${formatDays(daysCovered)} days × ${daysInMonth} days (today: ${Math.floor(todayMinutes / 60)} h ${
      todayMinutes % 60
    } min of 24 h, Berlin time)`,
  hoverWithoutToday: (daysCovered: number, daysInMonth: number) =>
    `so far ÷ ${daysCovered} completed days × ${daysInMonth} days`,
  noData: "Not enough data yet",
};

/** The hover explaining how a projection was worked out; depends on whether today's partial day is counted. */
export const projectionHover = (
  projection: { daysCovered: number; daysInMonth: number; todayMinutes: number | null },
  liveDayIncluded: boolean,
): string =>
  liveDayIncluded && projection.todayMinutes !== null
    ? PROJECTION_TEXTS.hoverWithToday(projection.daysCovered, projection.daysInMonth, projection.todayMinutes)
    : PROJECTION_TEXTS.hoverWithoutToday(projection.daysCovered, projection.daysInMonth);
